import { Vector2 } from "../../../common/vector2.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { Vfx2DManager } from "../../../webgl_2d/vfx_2d_manager.js";
import { IController } from "../../../webgl_2d/core/types/controller_types.js";
import {
  BuyBoosterType,
  getBoosterTextVariableParams,
} from "../../dialog/buy_booster_types.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { AbstractOverlay } from "../abstract_overlay.js";
import { OverlayId } from "../overlay_ids.js";
import { OverlayManager } from "../overlay_manager.js";
import { audioController } from "../../../audio/audio_controller.js";
import { AudioClips } from "../../../audio/audio_constants.js";
import { CumulativeBoosterID } from "../../../cumulative_boosters/cumulative_booster_id.js";

export interface NewBoosterOverlayParams
{
  booster_type?: BuyBoosterType;
  booster_id?: CumulativeBoosterID;
  effect_value?: number;
  on_claim?: () => void;
}

interface NewBoosterVisualConfig
{
  title_key: string;
  description_key: string;
  icon_id: ImgID;
}

const BOOSTER_VISUAL_CONFIGS: Record<BuyBoosterType, NewBoosterVisualConfig> = {
  [BuyBoosterType.Firework]: {
    title_key: "BOOSTER.FIREWORK",
    description_key: "DESCRIPTION.FIREWORK",
    icon_id: ImgID.UI_GameStartFireworkBooster,
  },
  [BuyBoosterType.FiveBalls]: {
    title_key: "BOOSTER.FIVE_BALLS",
    description_key: "DESCRIPTION.FIVE_BALLS",
    icon_id: ImgID.UI_GameStartMultiballsBooster,
  },
  [BuyBoosterType.BigBall]: {
    title_key: "BOOSTER.BIG_BALL",
    description_key: "DESCRIPTION.BIG_BALL",
    icon_id: ImgID.UI_GameStartBigBallBooster,
  },
  [BuyBoosterType.InfiniteShots]: {
    title_key: "BOOSTER.INFINITE_SHOTS",
    description_key: "DESCRIPTION.INFINITE_SHOTS",
    icon_id: ImgID.UI_GameStartInfiniteBallsBooster,
  },
};

const PASSIVE_BOOSTER_VISUAL_CONFIGS: Partial<Record<CumulativeBoosterID, NewBoosterVisualConfig>> = {
  [CumulativeBoosterID.Mine]: {
    title_key: "PASSIVE.BOOSTER.MINES",
    description_key: "DESCRIPTION.MINES",
    icon_id: ImgID.UI_PassiveBoosterBomb,
  },
};

export class NewBoosterOverlay extends AbstractOverlay
{
  private glow_controller: IController | null = null;
  private claimed = false;
  private readonly on_claim?: () => void;

  public constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    params: NewBoosterOverlayParams | undefined,
    overlay_id: OverlayId
  )
  {
    super( ctx, x, y, width, height, overlay_id, params, false );
    void audioController.playSound( AudioClips.BOOSTER_APPEAR );
    this.on_claim = params?.on_claim;

    const passive_config = params?.booster_id !== undefined
      ? PASSIVE_BOOSTER_VISUAL_CONFIGS[params.booster_id]
      : undefined;
    const booster_type = params?.booster_type ?? BuyBoosterType.BigBall;
    const config = passive_config ?? BOOSTER_VISUAL_CONFIGS[booster_type];
    const text_variable_params = passive_config
      ? []
      : getBoosterTextVariableParams( booster_type, params?.effect_value );
    const center_x = this.rect.getW2();
    const center_y = this.rect.getH2();

    const unlock_title_w = LayoutScaler.widthFromDesign( 820 );
    const unlock_title = new UITextElement(
      this.ctx,
      center_x - unlock_title_w / 2,
      center_y - LayoutScaler.heightFromDesign( 669 ),
      unlock_title_w,
      LayoutScaler.heightFromDesign( 237 ),
      {
        localization_key: "BOOSTER.UNLOCKED",
        font_size: LayoutScaler.textSizeFromDesign( 100 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 60 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 14 ),
        outline_color: "#E58900",
        auto_wrap: true,
        fit_font_size: true,
        line_height: 0.9,
        ...TextStyleScheme.buttonShadow(),
      }
    );
    this.addChild( unlock_title );

    const booster_name_w = LayoutScaler.widthFromDesign( 560 );
    const booster_name = new UITextElement(
      this.ctx,
      center_x - booster_name_w / 2,
      center_y - LayoutScaler.heightFromDesign( 389 ),
      booster_name_w,
      LayoutScaler.heightFromDesign( 109 ),
      {
        localization_key: config.title_key,
        text_variable_params,
        font_size: LayoutScaler.textSizeFromDesign( 68 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 48 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        fit_font_size: true,
        auto_wrap: false,
        ...TextStyleScheme.buttonShadow(),
      }
    );
    this.addChild( booster_name );

    const icon_size = LayoutScaler.widthFromDesign( 256 );
    const icon_x = center_x - icon_size / 2;
    const icon_y = center_y - LayoutScaler.heightFromDesign( 140 );
    const icon = new UIImage(
      this.ctx,
      icon_x,
      icon_y,
      icon_size,
      icon_size,
      config.icon_id
    );
    this.addChild( icon );

    const icon_absolute = icon.getAbsolutePosition();
    this.glow_controller = Vfx2DManager.addCoinCounterGlow(
      new Vector2(
        icon_absolute.x + icon_size / 2,
        icon_absolute.y + icon_size / 2
      ),
      LayoutScaler.widthFromDesign( 220 ),
      performance.now(),
      1000,
      true,
      180,
      true,
      0.35
    );

    const description_w = LayoutScaler.widthFromDesign( 748 );
    const description = new UITextElement(
      this.ctx,
      center_x - description_w / 2,
      center_y + LayoutScaler.heightFromDesign( 223 ),
      description_w,
      LayoutScaler.heightFromDesign( 82 ),
      {
        localization_key: config.description_key,
        text_variable_params,
        font_size: LayoutScaler.textSizeFromDesign( 52 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 38 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        fit_font_size: true,
        line_height: 1.05,
        ...TextStyleScheme.buttonShadow(),
      }
    );
    this.addChild( description );

    const button_w = LayoutScaler.widthFromDesign( 570 );
    const button_h = LayoutScaler.heightFromDesign( 250 );
    const claim_button = new UINineSliceElement(
      this.ctx,
      center_x - button_w / 2 + LayoutScaler.widthFromDesign( 11 ),
      center_y + LayoutScaler.heightFromDesign( 418 ),
      button_w,
      button_h,
      ImgID.UI_GreenFrameBtn,
      "frame_btn",
      undefined,
      undefined,
      true
    );
    claim_button.addChild( new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 9 ),
      LayoutScaler.yFromDesign( 8 ),
      button_w,
      button_h,
      {
        localization_key: "GET",
        font_size: LayoutScaler.textSizeFromDesign( 72 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        ...TextStyleScheme.buttonShadow(),
        outline_color: ColorScheme.more_lives_buy_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_offset_y: LayoutScaler.yFromDesign( 2 ),
      }
    ), true );
    claim_button.setOnClick( () =>
    {
      this.claimed = true;
      OverlayManager.closeOverlay();
    } );
    this.addChild( claim_button );
  }

  public name(): string
  {
    return "NewBoosterOverlay";
  }

  public override onOverlayWillClose(): void
  {
    this.glow_controller?.stop();
    this.glow_controller = null;

    // OverlayManager removes the overlay from its stack before this hook, so
    // the tutorial starts only after the unlock overlay is actually closed.
    if ( this.claimed )
      this.on_claim?.();
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }
}
